'use client'

import React, { useState, useCallback, useEffect } from 'react'

import type { CarouselBlock as CarouselBlockProps, Media as MediaType } from '@/payload-types'
import { Media } from '@/components/Media'
import { ArrowButton } from '@/components/ui/arrow-button'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'

type Props = CarouselBlockProps & {
  disableInnerContainer?: boolean
}

type SlideCardProps = {
  image: string | MediaType
  title: string
  description: string
  className?: string
}

const SlideCard: React.FC<SlideCardProps> = ({ image, title, description, className }) => (
  <div className={cn('flex flex-col gap-8 items-end', className)}>
    <div className="relative w-full h-[528px] rounded-[16px] overflow-hidden">
      <Media resource={image} className="w-full h-full object-cover" imgClassName="w-full h-full object-cover" />
    </div>
    <div className="font-medium tracking-tight text-black w-full animate-fadeIn text-xl lg:text-2xl leading-7">
      <p className="mb-0 lg:mb-2">{title}</p>
      <p className="text-black/70 mb-0">{description}</p>
    </div>
  </div>
)

type NavigationBarProps = {
  onPrev: () => void
  onNext: () => void
  disabled: boolean
}

const NavigationBar: React.FC<NavigationBarProps> = ({ onPrev, onNext, disabled }) => (
  <div className="flex items-center justify-between w-full">
    <div className="h-px bg-civo-cloud flex-1" />
    <div className="flex gap-3 items-center ml-4">
      <ArrowButton
        backgroundColor="white"
        size="medium"
        variant="minimal"
        direction="left"
        onClick={onPrev}
        disabled={disabled}
        className="border border-black"
        animated
      />
      <ArrowButton
        backgroundColor="white"
        size="medium"
        variant="minimal"
        direction="right"
        onClick={onNext}
        disabled={disabled}
        className="border border-black"
        animated
      />
    </div>
  </div>
)

export const TextUnderCarousel: React.FC<Props> = ({
  slides = [],
  disableInnerContainer,
  enableAutoPlay = false,
  autoPlayDelay = 5000,
  paddingTop,
  paddingBottom,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const nextSlide = useCallback(() => {
    if (slides && slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }
  }, [slides])

  const prevSlide = useCallback(() => {
    if (slides && slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
    }
  }, [slides])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        prevSlide()
      } else if (event.key === 'ArrowRight') {
        nextSlide()
      }
    },
    [nextSlide, prevSlide],
  )

  const getSlideAtPosition = useCallback(
    (position: number) => {
      if (!slides || slides.length === 0) return null
      const index = (currentSlide + position) % slides.length
      return slides[index]
    },
    [currentSlide, slides],
  )

  useEffect(() => {
    if (!enableAutoPlay || isPaused || !slides || slides.length <= 1) {
      return
    }

    const interval = setInterval(() => {
      nextSlide()
    }, autoPlayDelay ?? 5000)

    return () => clearInterval(interval)
  }, [enableAutoPlay, isPaused, autoPlayDelay, slides, nextSlide])

  if (!slides || slides.length === 0) {
    return null
  }

  const currentSlideData = slides[currentSlide]
  const isNavigationDisabled = slides.length <= 1

  return (
    <div
      className={cn('bg-civo-white', paddingClasses, {
        container: !disableInnerContainer,
      })}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      tabIndex={0}
      role="region"
      aria-label="Image carousel"
    >
      {/* Mobile Layout - Single slide with text alongside navigation */}
      <div className="flex flex-col gap-8 md:hidden">
        <div className="relative w-full h-[528px] rounded-[16px] overflow-hidden">
          {currentSlideData?.image && (
            <Media
              resource={currentSlideData.image}
              className="w-full h-full object-cover"
              imgClassName="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="flex gap-8 items-center justify-between w-full">
          <div
            key={currentSlide}
            className="flex-1 font-medium text-xl tracking-tight text-black animate-fadeIn"
          >
            <p className="mb-0">{currentSlideData?.title}</p>
            <p className="text-black/70 mb-0">{currentSlideData?.description}</p>
          </div>
          <div className="flex gap-3 items-center">
            <ArrowButton
              backgroundColor="white"
              size="medium"
              variant="minimal"
              direction="left"
              onClick={prevSlide}
              disabled={isNavigationDisabled}
              className="border border-black"
              animated
            />
            <ArrowButton
              backgroundColor="white"
              size="medium"
              variant="minimal"
              direction="right"
              onClick={nextSlide}
              disabled={isNavigationDisabled}
              className="border border-black"
              animated
            />
          </div>
        </div>
      </div>

      {/* Tablet & Desktop Layout - 2 slides on tablet, 3 on desktop */}
      <div className="hidden md:flex flex-col gap-[42px] items-center w-full">
        <div className="flex gap-[22px] items-start w-full">
          {[0, 1, 2].map((position) => {
            const slide = getSlideAtPosition(position)
            if (!slide) return null
            return (
              <SlideCard
                key={`${currentSlide}-${position}`}
                image={slide.image}
                title={slide.title}
                description={slide.description}
                className={cn('flex-1', {
                  'hidden lg:flex': position === 2, // Third slide only on desktop
                })}
              />
            )
          })}
        </div>
        <NavigationBar onPrev={prevSlide} onNext={nextSlide} disabled={isNavigationDisabled} />
      </div>
    </div>
  )
}
